import { FabricText, Group, Line, Rect, Textbox } from 'fabric';
import type { FabricObject } from 'fabric';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type {
  ContentSourceSpec,
  FillValue,
  ParamField,
  StandardContentPayload,
  ToolRecipe,
} from '../types';

/**
 * Word Wall — a per-standard vocabulary wall: a titled band above a responsive
 * grid of word cards (the term big, its definition beneath). Cross-subject but
 * ELA-homed (vocabulary instruction). Excellent fully manual — a teacher types
 * words + definitions and gets a print-ready wall — and Smart-fillable: declare
 * contentSource so the graph seam can populate the words from a standard's
 * vocabulary.
 *
 * Stock primitives only (Rect / Line / Textbox / FabricText), grouped and
 * tagged {tool,version,params} — plain fabric JSON, vector at any dpi. Params
 * arrive untrusted (teacher-typed OR replayed from stored JSON OR proposed by
 * the graph), so every read coerces and generate() never throws / never emits
 * NaN geometry.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the callouts.ts / elementCard.ts pattern).
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}
function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}
function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}
function jsonArray<T>(v: unknown, fallback: T[]): T[] {
  let parsed: unknown = v;
  if (typeof v === 'string') {
    try {
      parsed = JSON.parse(v);
    } catch {
      return fallback;
    }
  }
  return Array.isArray(parsed) ? (parsed as T[]) : fallback;
}
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** One representative solid color out of any FillValue (for readable-ink math). */
function solidFromFill(fill: unknown, fallback: string): string {
  if (typeof fill === 'string') return fill.trim() || fallback;
  if (fill && typeof fill === 'object') {
    const f = fill as { type?: string; color?: string; from?: string; stops?: { color?: string }[] };
    if (f.type === 'solid' && typeof f.color === 'string') return f.color;
    if ((f.type === 'linear-gradient' || f.type === 'radial-gradient')) {
      if (Array.isArray(f.stops) && f.stops.length && f.stops[0].color) return f.stops[0].color;
      if (typeof f.from === 'string') return f.from;
    }
  }
  return fallback;
}

/** Black or white ink, whichever reads on a #rrggbb background. */
function readableInkOn(bg: string, ink: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(bg).trim());
  if (!m) return ink;
  const n = parseInt(m[1], 16);
  const L = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return L > 0.6 ? ink : '#ffffff';
}

const INK = '#0f172a';
const DEFAULT_FONT = 'Nunito';
const SECTION_CONTENT = 'Content';
const SECTION_STYLE = 'Style';
const SECTION_LAYOUT = 'Layout';
const SECTION_COLORS = 'Colors';
const SECTION_FONTS = 'Fonts';

const MAX_WORDS = 24;
// A word wall AIMS for >=6 words, but that target is delivered by the FILL —
// the standards service tops thin graph vocabulary up to >=6 on-topic terms,
// and the draft prompt asks for 6-10 — NOT by a hard schema floor. A hard
// minItems:6 blocked valid 5-term fills from inserting (owner 2026-07-24), so
// the schema floor is 1: a fill is never un-committable, manual walls any size.

interface WordItem {
  term?: unknown;
  definition?: unknown;
  showDefinition?: unknown;
}

/** Good real defaults — never lorem (owner rule). A fractions word set. */
const SAMPLE_WORDS: WordItem[] = [
  { term: 'numerator', definition: 'The top number in a fraction. It tells how many parts you have.', showDefinition: true },
  { term: 'denominator', definition: 'The bottom number in a fraction. It tells how many equal parts are in the whole.', showDefinition: true },
  { term: 'equivalent', definition: 'Two fractions that name the same amount, like 1/2 and 2/4.', showDefinition: true },
  { term: 'simplify', definition: 'To rewrite a fraction with the smallest numbers that name the same amount.', showDefinition: true },
  { term: 'mixed number', definition: 'A whole number and a fraction together, like 2 1/3.', showDefinition: true },
  { term: 'unit fraction', definition: 'A fraction with 1 as the numerator, like 1/4.', showDefinition: true },
];

const WORD_ITEM_SCHEMA: ParamField[] = [
  { key: 'term', label: 'Word', type: 'text' },
  { key: 'showDefinition', label: 'Show definition', type: 'boolean', help: 'Off = word-only card.' },
  {
    key: 'definition',
    label: 'Definition',
    type: 'multiline',
    help: 'Shown beneath the word.',
    // Editor nicety (SM-F2): hide the field when this row is word-only. The
    // value is kept (never cleared), so flipping the toggle back reveals it.
    visibleWhen: { key: 'showDefinition', equals: true },
  },
];

// ---------------------------------------------------------------------------
// Smart-fill seam — populate words from a standard's vocabulary.
// ---------------------------------------------------------------------------

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  wants: ['vocabulary'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    // Merge INTO a copy of current so the educator's styling params (columns,
    // fills, fonts, card style) are never dropped. Only overwrite a field when
    // the payload actually carries content for it.
    const next = { ...current };
    const vocab = Array.isArray(payload?.vocabulary) ? payload.vocabulary : [];
    if (vocab.length) {
      // NO padding (owner 2026-07-24: a 5-term fill topped up with a stale
      // fraction word — an off-topic word is worse than a missing one). The
      // MIN_WORDS floor is the DRAFT side's job (hard-minimum prompt + thin
      // retry); a rare thin fill ships as-is for the teacher to extend.
      next.words = vocab.slice(0, MAX_WORDS).map((v) => ({
        term: v?.term ?? '',
        definition: v?.definition ?? '',
        showDefinition: true,
      }));
    }
    // Gate the title on words actually landing (owner 2026-07-24): a title-only
    // patch on an empty payload would misfire the modal's "changed" detection
    // (false "Filled from" banner + suppressed enrichment poll).
    const code = payload?.standard?.code;
    if (code && vocab.length) next.title = `Word Wall — ${code}`;
    // Provenance (survives regenerate — declared schema fields).
    if (payload?.standard?.code) next.sourceStandardCode = payload.standard.code;
    if (payload?.standard?.id) next.sourceStandardId = payload.standard.id;
    return next;
  },
};

const wordWall: ToolRecipe = {
  id: 'word-wall',
  domain: 'ela',
  category: 'decor',
  tags: ['vocabulary', 'word-wall', 'standards', 'literacy', 'terms', 'anchor'],
  featured: true,
  name: 'Word Wall',
  version: 1,
  description:
    'A vocabulary word wall — a titled grid of word cards with definitions. Type your own words or fill them from a standard.',
  defaults: {
    title: 'Word Wall',
    words: SAMPLE_WORDS.map((w) => ({ ...w })),
    showDefinitions: true,
    columns: 3,
    cardStyle: 'tile',
    headerFill: '#2563eb',
    cardFill: '#ffffff',
    textColor: INK,
    textFont: DEFAULT_FONT,
    titleFont: '',
    termFont: '',
    defFont: '',
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    { key: 'title', label: 'Title', type: 'text', section: SECTION_CONTENT, help: 'The band above the wall. Leave empty for none.' },
    {
      key: 'words',
      label: 'Words',
      type: 'list',
      itemSchema: WORD_ITEM_SCHEMA,
      summaryKeys: ['term'],
      minItems: 1,
      maxItems: MAX_WORDS,
      section: SECTION_CONTENT,
      help: 'One card per word. Open a row to add its definition.',
    },
    {
      key: 'showDefinitions',
      label: 'Show definitions',
      type: 'boolean',
      section: SECTION_CONTENT,
      help: 'Master switch — off makes a word-only wall (used by the K-2 preset).',
    },
    {
      key: 'columns',
      label: 'Columns',
      type: 'number',
      min: 2,
      max: 4,
      step: 1,
      section: SECTION_LAYOUT,
      help: 'Fewer columns = bigger cards.',
    },
    {
      key: 'cardStyle',
      label: 'Card style',
      type: 'select',
      section: SECTION_STYLE,
      options: [
        { value: 'tile', label: 'Tile (rounded)' },
        { value: 'ticket', label: 'Ticket (perforated)' },
        { value: 'cloud', label: 'Cloud (soft)' },
      ],
    },
    { key: 'headerFill', label: 'Title band', type: 'fill', section: SECTION_COLORS, help: 'Solid, gradient, or texture.' },
    { key: 'cardFill', label: 'Card fill', type: 'fill', section: SECTION_COLORS, help: 'Solid, gradient, or texture.' },
    { key: 'textColor', label: 'Text color', type: 'color', section: SECTION_COLORS },
    { key: 'textFont', label: 'Text font (default for all parts)', type: 'font', section: SECTION_FONTS },
    { key: 'titleFont', label: 'Title font', type: 'font', section: SECTION_FONTS },
    { key: 'termFont', label: 'Word font', type: 'font', section: SECTION_FONTS },
    { key: 'defFont', label: 'Definition font', type: 'font', section: SECTION_FONTS, visibleWhen: { key: 'showDefinitions', equals: true } },
    { key: 'sourceStandardCode', label: 'Source standard code', type: 'text', section: SECTION_CONTENT, advanced: true, help: 'Set automatically when filled from a standard.' },
    { key: 'sourceStandardId', label: 'Source standard id', type: 'text', section: SECTION_CONTENT, advanced: true, help: 'Set automatically when filled from a standard.' },
  ],
  modal: {
    hero: ['title', 'words'],
    columns: 2,
    sectionOrder: [SECTION_CONTENT, SECTION_STYLE, SECTION_LAYOUT, SECTION_COLORS, SECTION_FONTS],
  },
  // Honest band values (schema members only): younger = fewer, bigger cards in
  // a friendly face, word-only at K-2; older = denser grids in an academic
  // face. Same font ladder as the shipped tools.
  gradeBands: {
    K2: { textFont: 'Fredoka', columns: 2, cardStyle: 'cloud', showDefinitions: false },
    G35: { textFont: 'Nunito', columns: 3, cardStyle: 'tile', showDefinitions: true },
    G68: { textFont: 'Lexend', columns: 3, cardStyle: 'ticket', showDefinitions: true },
    G912: { textFont: 'Arial', columns: 4, cardStyle: 'tile', showDefinitions: true },
  },
  contentSource,
  async generate(params) {
    const W = 960;
    const pad = 32;
    const gap = 24;
    const innerPad = 18;
    const titleBandH = 108;

    const title = rawStr(params.title, '').trim();
    const showDefs = bool(params.showDefinitions, true);
    const columns = clamp(Math.round(num(params.columns, 3)), 2, 4);
    const styleRaw = str(params.cardStyle, 'tile');
    const cardStyle = styleRaw === 'ticket' || styleRaw === 'cloud' ? styleRaw : 'tile';

    const textColor = str(params.textColor, INK);
    const textFont = str(params.textFont, DEFAULT_FONT);
    const partFont = (key: string): string => {
      const v = params[key];
      return typeof v === 'string' && v.trim() !== '' ? v : textFont;
    };
    const titleFont = partFont('titleFont');
    const termFont = partFont('termFont');
    const defFont = partFont('defFont');

    const headerFill = (params.headerFill ?? '#2563eb') as FillValue;
    const cardFill = (params.cardFill ?? '#ffffff') as FillValue;

    // Junk rows dropped (need a non-empty term); empty result → sample set so a
    // wall is never blank on a poster.
    let words = jsonArray<WordItem>(params.words, SAMPLE_WORDS)
      .filter((w): w is WordItem => Boolean(w) && typeof w === 'object')
      .filter((w) => rawStr(w.term, '').trim() !== '')
      .slice(0, MAX_WORDS);
    if (words.length === 0) words = SAMPLE_WORDS;
    // NOTE: no sample top-up for 1–5 words (owner 2026-07-24: samples are
    // off-topic next to real content — worse than a shorter wall). MIN_WORDS
    // is enforced where words are AUTHORED: list UI minItems + the draft
    // side's hard-minimum prompt/retry.

    const cardW = (W - pad * 2 - (columns - 1) * gap) / columns;
    // Card text scales with card width (so fewer columns → bigger words),
    // clamped to sane poster sizes.
    const termSize = clamp(cardW * 0.14, 22, 46);
    const defSize = clamp(cardW * 0.075, 15, 23);
    const textW = Math.max(20, cardW - innerPad * 2);
    const rows = Math.ceil(words.length / columns);

    // Pass 1 — build (and thereby measure) every card's texts; compute the
    // uniform card height from the tallest content so the grid aligns.
    interface Built {
      term: Textbox;
      def: Textbox | null;
      contentH: number;
    }
    const built: Built[] = words.map((w) => {
      const term = new Textbox(rawStr(w.term, 'Word'), {
        width: textW,
        fontSize: termSize,
        fontFamily: termFont,
        fontWeight: 'bold',
        fill: textColor,
        textAlign: 'center',
        originX: 'center',
        originY: 'top',
      });
      const defText = rawStr(w.definition, '').trim();
      const showThis = showDefs && bool(w.showDefinition, true) && defText !== '';
      const def = showThis
        ? new Textbox(defText, {
            width: textW,
            fontSize: defSize,
            fontFamily: defFont,
            fill: textColor,
            textAlign: 'center',
            originX: 'center',
            originY: 'top',
          })
        : null;
      const contentH = (term.height ?? termSize) + (def ? 12 + (def.height ?? defSize) : 0);
      return { term, def, contentH };
    });

    const cardH = Math.max(...built.map((b) => b.contentH)) + innerPad * 2;

    const contentTop = (title ? titleBandH : 0) + 28;
    const H = contentTop + rows * cardH + (rows - 1) * gap + pad;

    const objects: FabricObject[] = [];
    const cardRects: FabricObject[] = [];
    const bandRects: FabricObject[] = [];

    // Transparent bounds so the group is exactly W × H regardless of content.
    objects.push(new Rect({ left: 0, top: 0, width: W, height: H, fill: 'transparent', strokeWidth: 0 }));

    // Title band.
    if (title) {
      const band = new Rect({ left: pad, top: pad, width: W - pad * 2, height: titleBandH - pad, rx: 20, ry: 20 });
      bandRects.push(band);
      objects.push(band);
      const bandSolid = solidFromFill(headerFill, '#2563eb');
      objects.push(
        new Textbox(title, {
          left: W / 2,
          top: pad + (titleBandH - pad) / 2,
          width: W - pad * 2 - 32,
          fontSize: 46,
          fontFamily: titleFont,
          fontWeight: 'bold',
          fill: readableInkOn(bandSolid, INK),
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
        }),
      );
    }

    // Pass 2 — lay the cards out on the grid.
    built.forEach((b, i) => {
      const col = i % columns;
      const row = Math.floor(i / columns);
      const x = pad + col * (cardW + gap);
      const y = contentTop + row * (cardH + gap);
      const cx = x + cardW / 2;

      const rx = cardStyle === 'cloud' ? Math.min(cardW, cardH) * 0.45 : cardStyle === 'ticket' ? 10 : 16;
      const card = new Rect({ left: x, top: y, width: cardW, height: cardH, rx, ry: rx });
      cardRects.push(card);
      objects.push(card);

      // Vertically center the content block inside the uniform card.
      let ty = y + (cardH - b.contentH) / 2;
      b.term.set({ left: cx, top: ty });
      objects.push(b.term);
      ty += (b.term.height ?? termSize) + 12;
      if (b.def) {
        // Ticket: a dashed perforation between word and definition.
        if (cardStyle === 'ticket') {
          objects.push(
            new Line([x + 14, ty - 6, x + cardW - 14, ty - 6], {
              stroke: textColor,
              strokeWidth: 1,
              strokeDashArray: [4, 4],
              opacity: 0.4,
            }),
          );
        }
        b.def.set({ left: cx, top: ty });
        objects.push(b.def);
      }
    });

    await Promise.all([
      ...bandRects.map((o) => applyFill(o, headerFill, '#2563eb')),
      ...cardRects.map((o) => applyFill(o, cardFill, '#ffffff')),
    ]);

    const group = new Group(objects);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, wordWall, params);
  },
};

registerRecipe(wordWall);
